import {
    EuiButton,
    EuiButtonEmpty,
    EuiFlexGroup,
    EuiFlexItem,
    EuiListGroup,
    EuiListGroupItem,
    EuiModal,
    EuiModalBody,
    EuiModalFooter,
    EuiModalHeader,
    EuiModalHeaderTitle,
    EuiOverlayMask,
} from "@elastic/eui";

import React, { useState, useRef, useEffect } from "react";
import { useIntl } from "react-intl";
import _ from "lodash";
import { readMultiFiles } from "../../../../utils/readFiles";
import { extractFileNameUrl } from "../../../../utils/url";
import ConfirmationModal from "../../../shared/modals/ConfirmationModal";

const buttonsSize = "s";

const mapFileToAttachment = ({ name, value }) => ({
    document: { name, url: value },
});

const generateDeleteModalContent = (attachment) => (
    <p>
        Are you sure you want to delete this document{" "}
        <b>{attachment.document.name || extractFileNameUrl(attachment.document.url)}</b>?
    </p>
);

const generateKey = () => Math.random().toString(36);

function DocumentsModal({
    onClose,
    onSubmit,
    defaultValue = [],
    onDelete,
    isLoading,
    isEditable,
    title,
}) {
    const [appendedFiles, setAppendedFiles] = useState([]);
    const [attachmentToDelete, setAttachmentToDelete] = useState(null);
    const [inputKey, setInputKey] = useState(generateKey());
    const intl = useIntl();

    useEffect(() => {
        clearInputValue();
    }, []);

    const clearInputValue = () => {
        const randomString = Math.random().toString(36);
        setInputKey(randomString);
    };

    const hiddenFileInput = useRef(null);

    const handleClick = (event) => {
        // Programatically click the hidden file input element
        // when the Button component is clicked
        hiddenFileInput.current.click();
    };

    const handleFileUpload = (event) => {
        const fileList = event.target.files;

        if (fileList.length === 0) {
            return;
        }

        readMultiFiles(
            fileList,
            (result) => {
                setAppendedFiles([...appendedFiles, ...result]);
            },
            (err) => {
                console.log(err);
            }
        );
    };

    const handleAttachmentDelete = (index) => {
        const deletedAttachment =
            index > defaultValue.length - 1
                ? appendedFiles[index - defaultValue.length]
                : defaultValue[index];

        setAttachmentToDelete(deletedAttachment);
    };

    const deleteAppendedAttachment = () => {
        const appendedFileIndex = appendedFiles.indexOf(attachmentToDelete);
        const appendedFilesClone = _.cloneDeep(appendedFiles);

        appendedFilesClone.splice(appendedFileIndex, 1);
        setAppendedFiles(appendedFilesClone);
        clearInputValue();
    };

    const deleteAttachment = () => {
        attachmentToDelete.id
            ? onDelete({ attachment: { ...attachmentToDelete, isDeleted: true } })
            : deleteAppendedAttachment();

        setAttachmentToDelete(null);
    };

    const handleSubmit = () => {
        const appendedDocuments = appendedFiles.map((file) => file.value);
        onSubmit({ attachment: appendedDocuments });
    };

    return (
        <EuiOverlayMask>
            <EuiModal
                onClose={onClose}
                style={{ minWidth: 500 }}
            >
                <EuiModalHeader>
                    <EuiModalHeaderTitle>{`${title || ""} ${intl.formatMessage({
                        id: "misc.documents",
                    })}`}</EuiModalHeaderTitle>
                </EuiModalHeader>

                <EuiModalBody>
                    {defaultValue.length + appendedFiles.length > 0 ? (
                        <EuiListGroup
                            maxWidth="100%"
                            flush
                        >
                            {[...defaultValue, ...appendedFiles.map(mapFileToAttachment)].map(
                                ({ id, document }, i) => {
                                    const fileName =
                                        document.name || extractFileNameUrl(document.url);
                                    return (
                                        <EuiListGroupItem
                                            key={id || i}
                                            id={id || i}
                                            onClick={() => {
                                                if (id) return;

                                                const newTab = window.open();
                                                newTab.document.body.innerHTML = `<img src="${document.url}" alt=${fileName}>`;
                                            }}
                                            label={fileName}
                                            href={id ? document.url : null}
                                            target="_blank"
                                            color="primary"
                                            isDisabled={isLoading}
                                            extraAction={{
                                                color: "danger",
                                                onClick: () => handleAttachmentDelete(i),
                                                iconType: "trash",
                                                iconSize: "s",
                                                isDisabled: isLoading,
                                                "aria-label": fileName,
                                                alt: "Delete attachment",
                                            }}
                                        />
                                    );
                                }
                            )}
                        </EuiListGroup>
                    ) : (
                        intl.formatMessage({ id: "trip-attachments.no-attachments-placeholder" })
                    )}
                </EuiModalBody>

                <EuiModalFooter>
                    <EuiFlexGroup
                        justifyContent="spaceBetween"
                        alignItems="center"
                    >
                        <EuiFlexItem grow={false}>
                            {isEditable && (
                                <EuiButton
                                    color="secondary"
                                    size={buttonsSize}
                                    onClick={handleClick}
                                    isLoading={isLoading}
                                >
                                    {intl.formatMessage({
                                        id: "trip-attachments.add-attachment-button-txt",
                                    })}
                                </EuiButton>
                            )}
                            <input
                                key={inputKey}
                                type="file"
                                multiple
                                ref={hiddenFileInput}
                                onChange={handleFileUpload}
                                style={{ display: "none" }}
                            />
                        </EuiFlexItem>

                        <EuiFlexItem grow={false}>
                            <EuiFlexGroup>
                                <EuiButtonEmpty
                                    size={buttonsSize}
                                    onClick={onClose}
                                    disabled={isLoading}
                                >
                                    {intl.formatMessage({
                                        id: "misc.cancel",
                                    })}
                                </EuiButtonEmpty>
                                {isEditable && (
                                    <EuiButton
                                        size={buttonsSize}
                                        onClick={handleSubmit}
                                        disabled={!appendedFiles.length || isLoading}
                                        fill
                                    >
                                        {intl.formatMessage({
                                            id: "misc.save",
                                        })}
                                    </EuiButton>
                                )}
                            </EuiFlexGroup>
                        </EuiFlexItem>
                    </EuiFlexGroup>
                </EuiModalFooter>
            </EuiModal>

            {attachmentToDelete && (
                <ConfirmationModal
                    title={intl.formatMessage({ id: "trip-attachments.delete-attachment-txt" })}
                    content={generateDeleteModalContent(
                        attachmentToDelete.id
                            ? attachmentToDelete
                            : mapFileToAttachment(attachmentToDelete)
                    )}
                    onConfirm={deleteAttachment}
                    onCancel={() => setAttachmentToDelete(null)}
                    confirmButtonText={intl.formatMessage({
                        id: "misc.delete",
                    })}
                />
            )}
        </EuiOverlayMask>
    );
}

export default DocumentsModal;
